/**
 * Questionnaire Page - Backend-Driven Flow
 * 
 * This page uses the DynamicFlowEngine which:
 * 1. Fetches all screen content from the backend API
 * 2. Renders screens dynamically based on screen_type
 * 3. Handles navigation and conditional logic server-side
 * 
 * NO HARDCODED SCREENS - everything comes from the backend JSON via API
 * 
 * FLOW PATHS:
 * - ES (Spain): Resident vs Non-Resident based on days_in_spain answer
 *   - Resident: days_in_spain = "Yes" or "I'm not sure"
 *   - Non-Resident: days_in_spain = "No" → shows "Situations That May Apply"
 * - US: E-file with IRS (MeF) screens
 */

import React, { useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import DynamicFlowEngine from '../components/flow/DynamicFlowEngine';

/** Allow skipping to a screen via URL (e.g. ?startAt=general_deductions). Set to "false" to disable. */
const ALLOW_START_AT = process.env.REACT_APP_FLOW_ALLOW_START_AT !== 'false';

const QuestionnairePage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const urlCountry = searchParams.get('country') || searchParams.get('countryCode'); // ES | US
  const urlStartAt = searchParams.get('startAt') || searchParams.get('start_at') || undefined;
  const urlRegion = searchParams.get('region') || undefined;

  // Country is selected inside the flow (choose_filing_country).
  // URL override is still supported for testing: ?country=ES or ?country=US
  const country = (urlCountry === 'ES' || urlCountry === 'US') ? urlCountry : null;
  const [taxYear] = useState(2024);

  const handleComplete = (summary: any) => {
    console.log('Flow completed:', summary);
    // Handle completion - e.g., redirect to dashboard, show results, etc.
  };

  return (
    <div className="h-screen flex flex-col min-h-0">
      {/* Reset + Screens index */}
      <div className="fixed top-4 left-4 z-50 flex items-center gap-2">
        <Link
          to="/screens"
          className="px-3 py-1.5 bg-white border border-gray-300 rounded-lg shadow-sm text-sm text-gray-600 hover:bg-gray-50 transition-colors"
        >
          All screens
        </Link>
      </div>
      <div className="flex-1 min-h-0 flex flex-col">
        <DynamicFlowEngine
          country={country}
          taxYear={taxYear}
          onComplete={handleComplete}
          startAtScreenId={ALLOW_START_AT ? urlStartAt : undefined}
          region={urlRegion}
        />
      </div>
    </div>
  );
};

export default QuestionnairePage;
